import { createSignal, onMount, Show, useContext } from 'solid-js';
import { StyledErrorMessage, StyledFieldContainer, StyledShowpadCodeContainer, StyledCurrentCodeBox } from './showpad-code-submission-style';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { addShowpadCode } from '../../tools/add-showpad-code';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { StyledTextInput } from '../../ui-components/inputs/text-input';
import { StyledFlexRow } from '../../ui-components/utility-style-components/flex';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { Modal } from '../../ui-components/modal/modal';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { Text } from '../../ui-components/text/text';
import { TabHandler } from '../menu/menu-desktop/tab-handling';
import { ShowpadDelayInfoBox } from '../showpad-delay-info-box/showpad-delay-info-box';
import { ErrorCatcher } from '../../tools/error-catcher';
import { styled } from 'solid-styled-components';

export const ShowpadCodeSubmission = () => {
    const [errorMessage, setErrorMessage] = createSignal('');
    const [showModal, setShowModal] = createSignal(false);
    const [code, setCode] = createSignal('');
    const [submitting, setSubmitting] = createSignal(false);
    const [success, setSuccess] = createSignal(false);

    const { 
        localize,
        userState,
        updateUser,
        contactUsPage,
    } = useContext(AppContext);

    // Accessibility/keyboard-controls related
    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    onMount(() => {
        window.addEventListener('keydown', keydownListener);
    });

    const closeModal = () => {
        setShowModal(false);
    };

    const keydownListener = (e: any) => {
        if (showModal() && e.key === 'Escape') {
            closeModal();
        }
    };

    const handleInputChange = (value: string) => {
        setSuccess(false);
        setErrorMessage('');
        setCode(value);
    };

    const submitCode = async () => {
        setSubmitting(true);
        setSuccess(false);
        setErrorMessage('');

        const { success, message } = await addShowpadCode({ showpadCode: code() }, updateUser);
        setSubmitting(false);

        if (!success) {
            if (message === 'invalid-showpad-code') {
                setErrorMessage(localize('invalid-showpad-code', 'The E-Learning access code you entered is invalid'));
                setShowModal(true);
            } else {
                console.error('Error:', message);
                setErrorMessage(localize('error-has-occurred', 'An error has occurred, please try again later'));
            }

            return;
        }

        setCode('');
        setSuccess(true);
    };

    const HelpModal = () => (

        <TabHandler refs={{
            firstTabbableElement,
            lastTabbableElement,
        }}>
            <Modal
                visible={true}
                onClose={() => closeModal()}
                size='small'
                darkenBackground={true}
                closeButtonRef={(el: HTMLElement) => setFirstTabbableElement(el)}
            >
                <StyledVerticalSpace size={5} />
                <Text fontSize='large' fontStyle='bold'>
                    {localize('no-code-modal-header', 'Get access to E-Learning courses')}
                </Text>

                <Text fontSize='normal' splitParagraph={true}>
                    {localize('no-code-modal-text', 'Please contact your local administrator to get an E-Learning access code to access the E-Learning courses.')}
                </Text>

                <StyledFlexRow gap='2rem' justifyContent='center' alignItems='center'>
                    <Button
                        label={localize('close-modal-button-text', 'Close')}
                        variant='secondary'
                        onClick={closeModal}
                    />

                    <Button
                        label={localize('contact-us', 'Contact us')}
                        isLink={true}
                        url={contactUsPage.url}
                        element={(el: HTMLElement) => {
                            setLastTabbableElement(el);
                        }}
                    />
                </StyledFlexRow>

            </Modal>
        </TabHandler>
    );


    return (

        <ErrorCatcher componentName='ShowpadCodeSubmission' logErrorToServer={true}>
            <Show when={userState.user}>
                <StyledShowpadCodeContainer>
                    <Heading variant='smallGray' tag='h2' noBlockSpacing>
                        {localize('course-access-code', 'E-Learning Access code')}
                    </Heading>

                    <StyledFieldContainer>
                        {/* --- Current Code --- */}
                        <Show when={userState.user?.showpadCode}>
                            <div>
                                <Text fontSize='normal' noBlockSpacing={true}>
                                    {localize('your-current-showpad-code', 'Your current access code')}
                                </Text>
                                <StyledCurrentCodeBox>
                                    {userState?.user?.showpadCode}
                                </StyledCurrentCodeBox>
                            </div>
                        </Show>

                        {/* --- New Code Input --- */}
                        <StyledVerticalSpace size={1} />

                        <Text fontSize='normal' noBlockSpacing={true}>
                            {localize('enter-new-code', 'Enter a new code to update your access')}
                        </Text>

                        <StyledFlexRow gap='1.5rem' flexWrap='wrap'>
                            <StyledTextInput
                                value={code()}
                                onInput={(e) => handleInputChange(e.currentTarget.value)}
                                placeholder={localize('enter-code-placeholder', 'Enter new access code')}
                                hasErrors={false}
                                name='showpad-code'
                                monospace
                                width='35rem'
                            />

                            <Button
                                label={localize('submit-code', 'Submit Access code')}
                                onClick={() => submitCode()}
                                variant='secondary'
                                noBlockSpacing
                                disabled={submitting() || !code()}
                            />

                            <Show when={submitting()}>
                                <LoadingSpinner size='2.2rem' />
                            </Show>
                        </StyledFlexRow>

                        <Show when={errorMessage()}>
                            <StyledErrorMessage>{errorMessage()}</StyledErrorMessage>
                        </Show>

                        <Show when={success()}>
                            <StyledVerticalSpace size={0.5} />
                            <ShowpadDelayInfoBox
                                withoutPadding={true}
                                text={localize(
                                    'showpad-code-successfully-added',
                                    'Your E-Learning access code has been successfully updated. Please note that it may take up to an hour for your access to be updated. Check back later to view your E-Learning courses.'
                                )}
                            />
                        </Show>
                    </StyledFieldContainer>
                </StyledShowpadCodeContainer>

                <Show when={showModal()}>
                    <HelpModal />
                </Show>
            </Show>
        </ErrorCatcher>
    );
};
